import { MaterialReservation } from '../interfaces/MaterialInventory.interface';
import { getApiErrorMessage } from './apiErrorMessage';

/**
 * The one cut status that means no piece has been cut yet. Mirrors
 * `MaterialReservation.NOT_CUT` in `material/models.py`, which is what
 * `MaterialReservationViewSet.destroy` compares against.
 */
export const CUT_STATUS_NOT_CUT = 'Not Cut';

/**
 * The status a finished reservation carries. Mirrors
 * `MaterialReservation.CUT`, and capitalised the way the API writes it — a
 * lowercase copy of this value sat in a guard for months and never matched.
 */
export const CUT_STATUS_CUT = 'Cut';

type ReservationCutFields = Pick<
	MaterialReservation,
	'material_cut_status' | 'actual_cut_quantity'
>;

/**
 * Whether a reservation has cut work behind it, and so can no longer be edited
 * or released: the cut shortened the bar and recorded its cost, and neither is
 * undone by removing the reservation.
 *
 * The cut status alone does not answer this, because a row can sit at
 * 'Not Cut' while carrying cut pieces, so the quantity is checked too. The API
 * enforces the same rule and refuses with 409; this only avoids offering an
 * action that would be refused.
 *
 * Every screen that offers Release reads the rule from here. Five of them do,
 * and when each carried its own copy four were wrong in the same way.
 */
export const hasRecordedCuts = (reservation: ReservationCutFields): boolean =>
	reservation.material_cut_status !== CUT_STATUS_NOT_CUT ||
	Number(reservation.actual_cut_quantity ?? 0) > 0;

/**
 * Whether the cut is finished, which is narrower than `hasRecordedCuts`: a row
 * can carry pieces while still being cut. Used to stop a reservation being
 * marked cut twice, where the second mark subtracts the reserved length from
 * the bar again.
 */
export const isFullyCut = (reservation: ReservationCutFields): boolean =>
	reservation.material_cut_status === CUT_STATUS_CUT;

export const RELEASE_TOOLTIP =
	'Remove this reservation and release the material back into inventory';

export const RELEASE_BLOCKED_TOOLTIP =
	'This reservation has cut work recorded against it and cannot be released';

export const releaseTooltip = (reservation: ReservationCutFields): string =>
	hasRecordedCuts(reservation) ? RELEASE_BLOCKED_TOOLTIP : RELEASE_TOOLTIP;

/**
 * What to tell the operator when the API refuses a release.
 *
 * The guard above keeps the action off the rows it can see, but the row on
 * screen can be stale — another tablet may have cut against the same bar since
 * the list loaded — so the refusal still has to be reported. Left unhandled it
 * is an unhandled promise rejection: no message, and no refetch, so the row
 * stays put and the operator clicks again.
 */
export const releaseErrorMessage = (error: unknown): string =>
	getApiErrorMessage(error, 'Failed to release the reservation');
